import React from 'react';
import {
  Grid,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Divider,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatDate } from '../../utils/helpers';

/**
 * ExperimentTreatmentsTable Component
 *
 * Displays a table of experiment treatments with their details.
 *
 * @param {Object} props
 * @param {Object} props.experiment - Experiment object
 */
function ExperimentTreatmentsTable({ experiment }) {
  const theme = useTheme();

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Divider
          sx={{
            my: 0.75,
            borderColor: theme.palette.divider,
            borderWidth: '1px',
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontSize: '0.9375rem',
            fontWeight: 600,
            color: theme.palette.text.primary,
            mb: 0.75,
            letterSpacing: '-0.01em',
          }}
        >
          Treatments ({experiment.treatments?.length || 0})
        </Typography>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px',
            boxShadow: 'none',
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: theme.palette.action.hover,
                }}
              >
                <TableCell
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    py: 1.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Name
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    py: 1.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Type
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    py: 1.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Template Version
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    py: 1.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Description
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    py: 1.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Hypothesis
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    py: 1.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Created
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {experiment.treatments?.map((treatment, index) => (
                <TableRow
                  key={treatment.id || index}
                  sx={{
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                    '&:last-child td': { borderBottom: 'none' },
                  }}
                >
                  <TableCell
                    sx={{
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      py: 1.5,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontSize: '0.875rem',
                        color: theme.palette.text.primary,
                      }}
                    >
                      {treatment.name}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      py: 1.5,
                    }}
                  >
                    <Chip
                      label={treatment.treatment_type}
                      size="small"
                      sx={{
                        backgroundColor:
                          treatment.treatment_type === 'control'
                            ? theme.palette.action.hover
                            : theme.palette.info.light,
                        color:
                          treatment.treatment_type === 'control'
                            ? theme.palette.text.primary
                            : theme.palette.info.dark,
                        border: `1px solid ${theme.palette.divider}`,
                        height: '28px',
                        minWidth: '80px',
                        width: '80px',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        '& .MuiChip-label': {
                          padding: '0 8px',
                          textTransform: 'capitalize',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      py: 1.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                        mb: 0.5,
                      }}
                    >
                      Version {treatment.template_version || 'Unknown'}
                    </Typography>
                    {treatment.template_modifications &&
                      Object.keys(treatment.template_modifications).length >
                        0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.75rem',
                            color: theme.palette.text.secondary,
                          }}
                        >
                          Modified
                        </Typography>
                      )}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      py: 1.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.875rem',
                        color: theme.palette.text.primary,
                      }}
                    >
                      {treatment.description || 'No description'}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      py: 1.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.875rem',
                        color: theme.palette.text.primary,
                      }}
                    >
                      {treatment.hypothesis || 'No hypothesis'}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      py: 1.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.875rem',
                        color: theme.palette.text.primary,
                      }}
                    >
                      {treatment.created_at
                        ? formatDate(treatment.created_at)
                        : 'Unknown'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </>
  );
}

export default ExperimentTreatmentsTable;
